import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface TwoFactorStatus {
  enabled: boolean;
  /** True when the tenant (or the super_admin rule) demands a second factor. */
  required: boolean;
  confirmed_at: string | null;
  /** Who demands it: "platform" (super_admin) or "organisation". */
  required_reason: 'platform' | 'organisation' | null;
  recovery_codes_remaining: number;
}

export interface TwoFactorSetup {
  /** Base32 seed, for people who type it into the app by hand. */
  secret: string;
  otpauth_uri: string;
  /** Rendered server-side so the portal needs no QR package. */
  qr_svg: string;
}

/**
 * Second factor: enrolment, recovery codes, and the tenant-wide switch.
 *
 * Two kinds of caller, one API. A signed-in user carries the normal session
 * and `NbAuthJWTInterceptor` attaches it. Someone caught by a freshly enabled
 * tenant requirement has no session at all — `/token` refused them and handed
 * out a short-lived enrolment token instead. That token lives here in memory
 * only: it is a credential, so it has no business in localStorage, and losing
 * it on a page reload is correct rather than unfortunate — the user simply
 * signs in again and gets a fresh one.
 */
@Injectable({ providedIn: 'root' })
export class TwoFactorService {
  private readonly base = environment.userBackend;
  private enrollmentToken: string | null = null;
  /** Why the enrolment is forced, so the page can name the right authority. */
  enrollmentReason: 'platform' | 'organisation' | null = null;

  constructor(private http: HttpClient) {}

  /** Called by the login form when /token answers `2fa_enrollment_required`. */
  beginForcedEnrollment(
    token: string,
    reason: 'platform' | 'organisation' | null = null,
  ): void {
    this.enrollmentToken = token;
    this.enrollmentReason = reason;
  }

  get hasForcedEnrollment(): boolean {
    return !!this.enrollmentToken;
  }

  endForcedEnrollment(): void {
    this.enrollmentToken = null;
    this.enrollmentReason = null;
  }

  private options() {
    // With an enrolment token there is no session for the interceptor to
    // attach, so the header is set here. When a session exists this returns
    // nothing and the interceptor does its usual job.
    if (!this.enrollmentToken) {
      return {};
    }
    return {
      headers: new HttpHeaders({ Authorization: `Bearer ${this.enrollmentToken}` }),
    };
  }

  status(): Promise<TwoFactorStatus> {
    return firstValueFrom(
      this.http.get<TwoFactorStatus>(`${this.base}/2fa/status`, this.options()),
    );
  }

  /** Mint a fresh secret + QR. Does not switch anything on yet. */
  setup(): Promise<TwoFactorSetup> {
    return firstValueFrom(
      this.http.post<TwoFactorSetup>(`${this.base}/2fa/setup`, {}, this.options()),
    );
  }

  /** Confirm the first code. Returns the recovery codes — shown exactly once. */
  activate(code: string): Promise<string[]> {
    return firstValueFrom(
      this.http
        .post<{ data: { recovery_codes: string[] } }>(
          `${this.base}/2fa/activate`,
          { code },
          this.options(),
        )
        .pipe(map((r) => r.data.recovery_codes)),
    );
  }

  disable(password: string, code?: string, recoveryCode?: string): Promise<void> {
    return firstValueFrom(
      this.http
        .post(`${this.base}/2fa/disable`, {
          password,
          code: code || null,
          recovery_code: recoveryCode || null,
        })
        .pipe(map(() => void 0)),
    );
  }

  regenerateRecoveryCodes(code: string): Promise<string[]> {
    return firstValueFrom(
      this.http
        .post<{ data: { recovery_codes: string[] } }>(
          `${this.base}/2fa/recovery-codes`,
          { code },
        )
        .pipe(map((r) => r.data.recovery_codes)),
    );
  }

  /** What the tenant admin configured. Distinct from `status().required`,
   *  which also returns true for super_admin regardless of the setting. */
  getRequirement(): Promise<boolean> {
    return firstValueFrom(
      this.http
        .get<{ data: { require_2fa: boolean } }>(`${this.base}/2fa/requirement`)
        .pipe(map((r) => r.data.require_2fa)),
    );
  }


  /** Tenant-wide switch. Admin only; the backend also insists the caller has
   *  their own factor active before it can be turned on. */
  setRequirement(require2fa: boolean): Promise<boolean> {
    return firstValueFrom(
      this.http
        .put<{ data: { require_2fa: boolean } }>(`${this.base}/2fa/requirement`, {
          require_2fa: require2fa,
        })
        .pipe(map((r) => r.data.require_2fa)),
    );
  }

  /** Clear another user's factor — the way back in after a lost device. */
  resetForUser(userId: string): Promise<void> {
    return firstValueFrom(
      this.http.post(`${this.base}/2fa/reset/${userId}`, {}).pipe(map(() => void 0)),
    );
  }
}
